import React, { memo, PropsWithChildren, useMemo } from 'react';
import classnames from 'classnames';
import LinkButton from '@/shared/LinkButton';
import noop from '@/utils/noop';
import SessionStorage from '@/utils/sessionStorage';
import is from '@/utils/is';
import Div from './Div';

type ButtonSizes = 'xs' | 'sm' | 'base' | 'lg' | 'xl';

type ButtonThemes =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'none'
  | 'icon'
  | 'link'
  | 'primaryOnColor';

const Loader = ({ size, theme }: { size: ButtonSizes; theme: ButtonThemes }) => (
  <Div
    className={classnames('flex items-center', {
      'h-6': size === 'base' || size === 'lg' || size === 'xl',
      'h-4.5': size === 'sm' || size === 'xs',
    })}
  >
    <Div
      className={classnames({
        'dotflashing-white': theme === 'primary',
        'dotflashing-black': theme === 'primaryOnColor',
      })}
      forceRender
    />
  </Div>
);

const THEMES = {
  primary:
    'bg-cm-primary rounded-cs text-cti-on-primary border border-transparent hover:bg-cm-primary transition-all', // focus:bg-cm-medium
  primaryOnColor: 'bg-cs-primary rounded-cs text-cti-primary border border-transparent', // focus:bg-cm-medium
  secondary:
    'bg-cs-primary rounded-cs text-cti-primary border border-cm-primary hover:bg-cm-medium hover:text-cti-on-primary transition-all hover:border-transparent ', // focus:bg-cm-subtle
  tertiary:
    'bg-cs-primary rounded-cs text-cti-primary border border-transparent hover:bg-cs-secondary hover:text-cti-primary transition-all  ', // focus:text-cti-tertiary
  none: '',
  link: 'text-blue-400 focus:text-blue-500 focus:no-underline	 hover:underline',
  icon: '',
};

const BUTTON_FONT_SIZES = {
  xs: '',
  sm: 'text-button-small', // Small
  base: 'text-button-normal', // Medium
  lg: '',
  xl: '',
};

const BUTTON_SPACING = {
  xs: 'px-4 py-1',
  sm: 'py-3 px-3', // Small as of now being used in link theme
  base: 'px-6 py-3', // Medium
  lg: ' px-7 py-2.5',
  xl: 'px-8 py-3',
};

const COMMON = 'text-center flex justify-center gap-s3 items-center';

export interface IButton {
  id?: string;
  theme?: ButtonThemes;
  type?: 'button' | 'submit' | 'reset';
  href?: string;
  target?: string;
  size?: ButtonSizes;
  className?: string;
  onClick?: (callback?: any) => void;
  label?: string;
  disabled?: boolean;
  isLoading?: boolean;
  dataTestId?: string;
  style?: any;
  prefetch?: boolean;
  external?: boolean;
  title?: string;
  track?: boolean;
  rounded?: boolean;
  scroll?: boolean;
  form?: string;
}

const Button = (
  {
    id,
    theme = 'primary',
    type = 'button',
    href,
    target,
    children,
    size = 'base',
    className,
    label,
    disabled,
    onClick,
    isLoading,
    dataTestId,
    prefetch,
    style,
    external,
    title,
    track = true,
    rounded = false,
    scroll,
    ...rest
  }: PropsWithChildren<IButton>,
  ref: any,
) => {
  const { isPrimaryTheme, isSecondaryTheme, isTertiaryTheme, isLinkTheme, isPrimaryOnColorTheme } =
    useMemo(
      () => ({
        isPrimaryTheme: theme === 'primary',
        isSecondaryTheme: theme === 'secondary',
        isTertiaryTheme: theme === 'tertiary',
        isLinkTheme: theme === 'link',
        isPrimaryOnColorTheme: theme === 'primaryOnColor',
      }),
      [theme],
    );
  const buttonClasses = useMemo(
    () =>
      classnames(
        COMMON,
        THEMES[theme],
        {
          [BUTTON_FONT_SIZES[size]]: theme !== 'icon' && theme !== 'none',
          [BUTTON_SPACING[size]]: theme !== 'icon' && theme !== 'none',
        },
        className,
        {
          '!px-0': isLinkTheme && size === 'base',
          'cursor-not-allowed': disabled || isLoading,
          // Disabled
          '!bg-cs-disabled !text-cti-disabled': disabled && isPrimaryTheme,
          '!border-co-bold !text-cti-disabled hover:bg-white': disabled && isSecondaryTheme,
          '!text-cti-disabled hover:bg-white  focus:bg-white': disabled && isTertiaryTheme,
          '!text-cti-disabled  hover:no-underline': disabled && isLinkTheme,
          '!text-cti-on-disabled !bg-cs-disabled': disabled && isPrimaryOnColorTheme,
          // Loading
          'hover:bg-neutral-700 focus:bg-neutral-700': isLoading && isPrimaryTheme,
          'hover:bg-white focus:bg-neutral-700': isLoading && isSecondaryTheme,
          'hover:bg-white focus:bg-white': isLoading && isTertiaryTheme,

          //Rounded

          '!rounded-cl': rounded,
        },
      ),
    [
      theme,
      size,
      className,
      disabled,
      isLoading,
      isPrimaryTheme,
      isSecondaryTheme,
      isTertiaryTheme,
      isLinkTheme,
      isPrimaryOnColorTheme,
      rounded,
    ],
  );

  const onClickCallback = (...args: any) => {
    if (track) {
      SessionStorage.set('SOURCE_CTA', {
        title: title || label || is.string(children) ? children : '',
      });
    }

    if (onClick) onClick.apply(null, args);
  };

  const onClickHandler = disabled ? noop : onClickCallback;

  if (href)
    return (
      <LinkButton
        id={id}
        scroll={scroll}
        external={external}
        prefetch={prefetch}
        title={title}
        dataTestId={dataTestId}
        className={buttonClasses}
        target={target}
        href={href}
        onClick={onClickHandler}
      >
        <>{children}</>
      </LinkButton>
    );
  return (
    <button
      id={id}
      ref={ref}
      data-testid={dataTestId}
      className={buttonClasses}
      type={type}
      style={style}
      aria-disabled={disabled}
      disabled={disabled}
      title={title}
      aria-label={title}
      onClick={onClickHandler}
      {...rest}
    >
      {isLoading ? <Loader size={size} theme={theme} /> : children || label || null}
    </button>
  );
};

export default memo(React.forwardRef(Button));
